import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { useBusinessConfig } from '@/hooks/useBusinessConfig';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Shield, CreditCard, Heart, LogOut, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export function SubscriptionExpiredPage() {
  const navigate = useNavigate();
  const { config } = useBusinessConfig();

  const isTrial = config?.subscription_status === 'trial';
  const trialDaysLeft = config?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(config.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;
  const expired = config?.subscription_status === 'expired' || config?.subscription_status === 'cancelled';

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleStripeCheckout = async (plan: 'starter' | 'pro') => {
    // Get user ID for Stripe metadata
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('You must be logged in');
      return;
    }

    // Placeholder Stripe Payment Links - replace with actual links
    const stripeLinks = {
      starter: 'https://buy.stripe.com/PLACEHOLDER_STARTER',
      pro: 'https://buy.stripe.com/PLACEHOLDER_PRO',
    };

    // Open Stripe checkout in new window
    window.open(stripeLinks[plan], '_blank');

    // Note: Stripe webhook will update subscription_status automatically
    toast.success('Redirecting to secure checkout...');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-500/20 rounded-full mb-4">
            {isTrial ? (
              <AlertTriangle className="w-10 h-10 text-red-400" />
            ) : (
              <Shield className="w-10 h-10 text-red-400" />
            )}
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tighter">
            {isTrial && trialDaysLeft <= 0
              ? 'Your Trial Has Ended'
              : expired
              ? 'Your Subscription Has Expired'
              : 'Account Access Restricted'}
          </h1>
          <p className="text-slate-300 text-lg max-w-xl mx-auto">
            {isTrial
              ? "Your 30-day free trial is over. Choose a plan to continue using VaultStack and keep all your data."
              : "Your subscription is no longer active. Upgrade now to restore access to your business vault."}
          </p>
        </div>

        {/* Data Safe Message */}
        <div className="bg-emerald-500/10 border-2 border-emerald-500/30 rounded-2xl p-6 backdrop-blur-sm">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0 mt-1" />
            <div>
              <h3 className="font-black text-emerald-300 uppercase mb-1">Your Data Is Safe</h3>
              <p className="text-emerald-100/80 text-sm">
                All your customers, quotes, invoices, and settings are securely stored and waiting for you. 
                Upgrade anytime to restore full access — nothing will be lost.
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Plans */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Starter Plan */}
          <div className="bg-white/5 border-2 border-white/10 backdrop-blur-sm rounded-[2rem] p-8 space-y-6 hover:border-purple-500/50 transition-colors">
            <div>
              <h3 className="text-2xl font-black text-white uppercase">Starter</h3>
              <p className="text-slate-400 text-sm mt-1">Perfect for solo operators</p>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-5xl font-black text-white">$19</span>
              <span className="text-slate-400 font-bold">/month AUD</span>
            </div>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-slate-300 text-sm">
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                Up to 30 customers
              </li>
              <li className="flex items-center gap-2 text-slate-300 text-sm">
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                Unlimited quotes & invoices
              </li>
              <li className="flex items-center gap-2 text-slate-300 text-sm">
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                Inventory management
              </li>
              <li className="flex items-center gap-2 text-slate-300 text-sm">
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                Booking system
              </li>
            </ul>
            {/* Stripe Buy Button - Starter Plan (Australia Only) */}
            <div className="mt-6">
              <a
                href="https://buy.stripe.com/?prefilled_email=0"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full h-14 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-black uppercase tracking-widest text-center leading-[3.5rem]"
              >
                Subscribe — $19/mo AUD
              </a>
              <p className="text-[9px] text-slate-500 font-bold uppercase mt-2 text-center">
                🇦🇺 Australia Only • Secure checkout via Stripe
              </p>
            </div>
          </div>

          {/* Pro Plan */}
          <div className="bg-gradient-to-br from-purple-600/20 to-slate-900/50 border-2 border-purple-500/50 backdrop-blur-sm rounded-[2rem] p-8 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-black text-white uppercase">Pro</h3>
              <span className="text-[10px] font-black bg-purple-500 text-white px-2 py-1 rounded uppercase">
                Most Popular
              </span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-5xl font-black text-white">$49</span>
              <span className="text-slate-400 font-bold">/month AUD</span>
            </div>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-slate-300 text-sm">
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                Unlimited customers
              </li>
              <li className="flex items-center gap-2 text-slate-300 text-sm">
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                Everything in Starter
              </li>
              <li className="flex items-center gap-2 text-slate-300 text-sm">
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                Priority support
              </li>
              <li className="flex items-center gap-2 text-slate-300 text-sm">
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                Advanced analytics
              </li>
            </ul>
            {/* Stripe Buy Button - Pro Plan (Australia Only) */}
            <div className="mt-6">
              <p className="text-[10px] text-slate-400 font-bold uppercase mb-2 text-center">
                Coming Soon • Australia Only
              </p>
              <Button
                onClick={() => handleStripeCheckout('pro')}
                className="w-full h-14 bg-white text-purple-900 hover:bg-purple-100 rounded-2xl font-black uppercase tracking-widest"
                disabled
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Pro Plan (Soon)
              </Button>
            </div>
          </div>
        </div>

        {/* NDIS Discount */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6 text-pink-400" />
            <h3 className="font-black text-white uppercase">NDIS & PWD Discount</h3>
          </div>
          <p className="text-slate-300 text-sm">
            Verified NDIS participants and pensioners receive <span className="font-black text-pink-400">50% off</span> or 
            <span className="font-black text-pink-400"> free access</span> to VaultStack. 
            We're committed to supporting Australian small business owners with disabilities.
          </p>
          <Button
            onClick={() => navigate('/upgrade')}
            variant="outline"
            className="border-pink-500/50 text-pink-400 hover:bg-pink-500/10 rounded-2xl font-black uppercase"
          >
            Verify NDIS Status
          </Button>
        </div>

        {/* International Users - Whop */}
        <div className="text-center space-y-3">
          <p className="text-slate-400 text-sm font-bold uppercase">Outside Australia?</p>
          {/* Whop Starter - $19.99 USD/mo */}
          <a
            href="https://whop.com/checkout/plan_kP4RvQFtMoGg1"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-white/5 border border-white/20 rounded-xl p-4 hover:border-purple-400 transition-colors"
          >
            <p className="text-[10px] font-black text-slate-400 uppercase mb-1">Starter Plan</p>
            <p className="text-lg font-black text-white">$19.99 <span className="text-sm font-bold text-slate-400">USD/mo</span></p>
            <p className="text-[9px] text-purple-400 font-bold mt-2">🌏 Instant Access via Whop</p>
          </a>
          {/* Whop Pro - $79.99 USD/mo */}
          <a
            href="https://whop.com/checkout/plan_k2ZoNTlw72XQX"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-gradient-to-r from-purple-600 to-purple-800 border-2 border-purple-500 rounded-xl p-4 hover:from-purple-500 hover:to-purple-700 transition-colors"
          >
            <p className="text-[10px] font-black text-white/80 uppercase mb-1">Pro Plan</p>
            <p className="text-lg font-black text-white">$79.99 <span className="text-sm font-bold text-white/70">USD/mo</span></p>
            <p className="text-[9px] text-white font-bold mt-2">⭐ Best Value • Instant Access</p>
          </a>
        </div>

        {/* Logout */}
        <div className="flex justify-center pt-8">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="text-slate-400 hover:text-white gap-2"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </div>
    </div>
  );
}
